<template>
  <div class="y-datepicker">
    <y-text
      v-if="showLabel"
      class="title"
      font="Barlow, Barlow"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.checkInOut") }}
    </y-text>
    <div class="content-show" @click="handleOpenDate">
      <font-icon class="icon" icon="icon-rili" />
      <div class="content-show-label flex">
        <y-text
          class="text-date"
          font="var(--y-medium-font)"
          color="#292929"
          size="18px"
        >
          {{ dateShow }}
        </y-text>
        <y-text
          color="#696969"
          class="text-night"
          font="var(--y-medium-font)"
          size="18px"
        >
          {{ t("shared.hotel.search.night", nightCount) }}
        </y-text>
      </div>
    </div>
    <div class="select-content" v-if="showDatePick">
      <el-date-picker
        ref="datePickRef"
        :disabled-date="disabledDate"
        @change="handleChange"
        v-model="date"
        type="daterange"
        placeholder="Pick a day"
        :first-day-of-week="0"
        popper-class="form-data-picker"
        @panel-change="handlePanelChange"
        :popper-append-to-body="false"
      >
        <template v-if="toggle" #default="cell">
          <div class="el-date-table-cell">
            <span class="el-date-table-cell__text">{{ cell.text }}</span>
            <span class="price" v-if="isHotelIdInRoute">{{
              ratesMap[dayjs(cell.date).format("YYYY-MM-DD")]
            }}</span>
          </div>
        </template>
      </el-date-picker>
    </div>
  </div>
</template>

<script setup>
import common from "@charmdeer/common";
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { getHotelRateCalendar } from "@/api/biz/openHotel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
const route = useRoute();

const isHotelIdInRoute = computed(() => {
  const query = route.query;
  if (query && query.hotelId!== hotelId.value) {
    hotelId.value = query.hotelId
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    fetchRateCalendar({ start: start, end: end });
  }
  return !!query.hotelId;
});

const YHotelStore = useHotelStoreHook();
const { t } = useI18n();
const emit = defineEmits(["update:modelValue", "change"]);
const props = defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  modelValue: Object
});
const toggle = common.runtime.toggle.isActive("Y_RATE_CALENDAR");
const date = ref([props.modelValue.checkIn, props.modelValue.checkOut]);
const ratesMap = ref({});
const hotelId = ref("");
const datePickRef = ref(null);
const showDatePick = ref(false);
const dateShow = computed(() => {
  const _start = date.value[0];
  const _end = date.value[1];
  if (!_start || !_end) {
    return "";
  }
  return `${dayjs(_start).format("DD/MM/YYYY")} - ${dayjs(_end).format(
    "DD/MM/YYYY"
  )}`;
});

watch(
  () => YHotelStore?.searchModel?.stay?.date,
  newVal => {
    date.value = [newVal?.checkIn, newVal?.checkOut];
  }
);

const nightCount = computed(() => {
  const _start = dayjs(date.value[0]);
  const _end = dayjs(date.value[1]);
  return _end.diff(_start, "day");
});

const handlePanelChange = data => {
  if (hotelId.value) {
    const dateOfLeft = data[0];
    const dateOfRight = data[1];
    const firstDayOfLeftMonth = dayjs(dateOfLeft).startOf("month").toDate();
    const lastDayOfRightMonth = dayjs(dateOfRight).endOf("month").toDate();
    fetchRateCalendar({ start: firstDayOfLeftMonth, end: lastDayOfRightMonth });
  }
};

const handleDateRange = dateRange => {
  const { start, end } = dateRange;
  if (start && end) {
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();
    const lastDayOfStartMonth = new Date(start.getFullYear(), startMonth + 1, 0);
    const lastDayOfEndMonth = new Date(end.getFullYear(), endMonth + 1, 0);
    let firstDayOfStartMonth = new Date(start.getFullYear(), startMonth, 1);
    const firstDayOfEndMonth = new Date(end.getFullYear(), endMonth, 1);

    const today = new Date();
    if (firstDayOfStartMonth < today) {
      firstDayOfStartMonth = today;
    }

    if (
      !Object.keys(ratesMap.value).includes(
        dayjs(lastDayOfStartMonth).format("YYYY-MM-DD")
      )
    ) {
      return { start: firstDayOfStartMonth, end: lastDayOfEndMonth };
    }
    if (
      !Object.keys(ratesMap.value).includes(
        dayjs(lastDayOfEndMonth).format("YYYY-MM-DD")
      )
    ) {
      return { start: firstDayOfEndMonth, end: lastDayOfEndMonth };
    }
  }
  return {}
};

const fetchRateCalendar = async dateRange => {
  const { start, end } = handleDateRange(dateRange);
  if (!start && !end ) return
  const newRatesMap = {};
  if (hotelId.value) {
    const res = await getHotelRateCalendar(
      { start: dayjs(start), end: dayjs(end) },
      hotelId.value
    );
    res?.results?.forEach(result => {
      const price = result.lowest?.price;
      newRatesMap[result.checkIn] = price != null ? Math.floor(price).toLocaleString() : "";
    });
    ratesMap.value = {
      ...ratesMap.value,
      ...newRatesMap
    };
  } else {
    ratesMap.value = {}
  }
};

const handleOpenDate = () => {
  showDatePick.value = true;
  nextTick(() => {
    datePickRef.value.focus(true);
  });
};

const handleChange = value => {
  if (dayjs(value[0]).isSame(dayjs(value[1]), "day")) {
    ElMessage.warning(t("shared.hotel.search.dateNotSame"));
    date.value = [
      dayjs(props.modelValue.checkIn).toDate(),
      dayjs(props.modelValue.checkOut).toDate()
    ];
    return;
  }

  showDatePick.value = false;
  emit("update:modelValue", { checkIn: value[0], checkOut: value[1] });
  emit("change", value);
};

const disabledDate = time => {
  return time.getTime() < Date.now() - 86400000;
};
</script>

<style lang="scss" scoped>
.y-datepicker {
  .title {
    margin-bottom: 8px;
    line-height: 1;
    display: block;
  }
  .content-show {
    display: flex;
    align-items: center;
    .icon {
      font-size: 18px;
    }
    .text-date {
      display: block;
      margin-left: 6px;
      margin-right: 16px;
    }
  }
  .select-content {
    display: flex;
    align-items: center;
  }
}

:deep(.el-range-editor.el-input__wrapper) {
  opacity: 0;
  z-index: -999;
  position: absolute;
}
</style>

<style>
.el-date-table-cell {
  .price {
    font-size: 8px;
    color: #949494;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 24px;
    display: block;
    margin-top: 14px;
  }
}
</style>
